let designMutationActive = false;
let coordinatedWorkspaceWriteDepth = 0;

interface SessionOnlyDesignMutationAuthority {
  baseWorkspaceHash: string;
  activeWorkspaceHash: string;
}

const sessionOnlyDesignMutationAuthorities = new Map<
  string,
  SessionOnlyDesignMutationAuthority
>();

export function assertNoOpenPencilDesignMutationActive(): void {
  if (designMutationActive && coordinatedWorkspaceWriteDepth === 0) {
    throw new Error("OPENPENCIL_DESIGN_MUTATION_IN_PROGRESS");
  }
}

export function withCoordinatedOpenPencilWorkspaceWrite<T>(
  write: () => T,
): T {
  coordinatedWorkspaceWriteDepth += 1;
  try {
    return write();
  } finally {
    coordinatedWorkspaceWriteDepth -= 1;
  }
}

export async function withOpenPencilDesignMutationGuard<T>(
  operation: () => Promise<T>,
): Promise<T> {
  assertNoOpenPencilDesignMutationActive();
  designMutationActive = true;
  try {
    return await operation();
  } finally {
    designMutationActive = false;
  }
}

/** Track a local/imported project's coordinated source+Cogo transition without
 * making either half durable. Later source-only edits must not persist the new
 * source revision by itself. A matching inverse transition back to the
 * original durable workspace clears the fence. */
export function transitionSessionOnlyDesignMutationAuthority(input: {
  projectId: string;
  previousWorkspaceHash: string;
  nextWorkspaceHash: string;
}): void {
  const current = sessionOnlyDesignMutationAuthorities.get(input.projectId);
  if (!current) {
    sessionOnlyDesignMutationAuthorities.set(input.projectId, {
      baseWorkspaceHash: input.previousWorkspaceHash,
      activeWorkspaceHash: input.nextWorkspaceHash,
    });
    return;
  }
  if (current.activeWorkspaceHash !== input.previousWorkspaceHash) {
    throw new Error("DESIGN_MUTATION_SESSION_ONLY_AUTHORITY_DIVERGED");
  }
  if (input.nextWorkspaceHash === current.baseWorkspaceHash) {
    sessionOnlyDesignMutationAuthorities.delete(input.projectId);
    return;
  }
  current.activeWorkspaceHash = input.nextWorkspaceHash;
}

export function assertSessionOnlySourcePersistenceAllowed(
  projectId: string,
): void {
  if (sessionOnlyDesignMutationAuthorities.has(projectId)) {
    throw new Error(
      "DESIGN_MUTATION_LOCAL_SESSION_ONLY_SOURCE_PERSISTENCE_BLOCKED",
    );
  }
}

/** Loading the old durable local snapshot starts a fresh project session. */
export function clearSessionOnlyDesignMutationAuthority(
  projectId: string,
): void {
  sessionOnlyDesignMutationAuthorities.delete(projectId);
}
